import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import OurLayouts from '../layout/index';
import axios from 'axios';
import { toast } from "react-toastify";
import { useNavigate } from 'react-router-dom';

const ChangePassword = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const token = localStorage.getItem("auth_token");
    const tenantId = localStorage.getItem("tenantId");
    const API_BASE = process.env.REACT_APP_API_URL;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        };

        if (!formData.currentPassword.trim()) {
            newErrors.currentPassword = 'Current password is required';
            valid = false;
        }

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required';
            valid = false;
        } else if (!/[A-Z]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one uppercase letter';
            valid = false;
        } else if (!/[a-z]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one lowercase letter';
            valid = false;
        } else if (!/[0-9]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one number';
            valid = false;
        } else if (!/[^A-Za-z0-9]/.test(formData.newPassword)) {
            newErrors.newPassword = 'Password must contain at least one special character';
            valid = false;
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your new password';
            valid = false;
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
            valid = false;
        }

        if (formData.currentPassword && formData.newPassword && 
            formData.currentPassword === formData.newPassword) {
            newErrors.newPassword = 'New password must be different from current password';
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };
    const handleLogout = async () => {
        try {
          const token = localStorage.getItem("auth_token");
          const tenantId = localStorage.getItem("tenantId");
    
          await fetch(`${process.env.REACT_APP_API_URL}auth/logout`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              tenantId: tenantId,
            },
          });
     localStorage.removeItem("auth_token");
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
        }
      };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        if (!validateForm()) {
            setIsSubmitting(false);
            return;
        }

        try {
            const queryParams = {
                currentPassword: formData.currentPassword,
                newPassword: formData.confirmPassword
            };

            const queryString = new URLSearchParams(queryParams).toString();

            const res = await axios.post(
                `${API_BASE}auth/change-password?${queryString}`,
                null, 
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        // tenantId: tenantId
                    }
                }
            );
            
            toast.success(res.data.message || "Password changed successfully");
            handleLogout();
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setErrors({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });

        } catch (err) {
            console.error("Password change error:", err.response || err);
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.message || 
                               "Failed to change password. Please try again.";
            toast.error(errorMessage);
            
            if (err.response?.status === 400 && err.response?.data?.error?.includes("current password")) {
                setErrors(prev => ({
                    ...prev,
                    currentPassword: err.response.data.error
                }));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <OurLayouts>
            <div className="w-full mx-auto bg-[#f1f8f7]  p-6 rounded-lg shadow-md mt-10 max-w-md">
                <h1 className="text-2xl font-bold text-[#004276] mb-6">Change Password</h1>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="mb-4">
                        <label className="block text-[#004276] font-medium mb-2" htmlFor="currentPassword">
                            Old Password *
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword.currentPassword ? "text" : "password"}
                                id="currentPassword"
                                name="currentPassword"
                                placeholder="Enter Old Password"
                                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                                    errors.currentPassword ? 
                                    'border-red-500 focus:ring-red-500' : 
                                    'border-gray-300 focus:ring-blue-500'
                                }`}
                                value={formData.currentPassword}
                                onChange={handleChange}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                onClick={() => togglePasswordVisibility('currentPassword')}
                            >
                                {showPassword.currentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {errors.currentPassword && (
                            <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="block text-[#004276] font-medium mb-2" htmlFor="newPassword">
                            New Password *
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword.newPassword ? "text" : "password"}
                                id="newPassword"
                                name="newPassword"
                                placeholder="Enter New Password"
                                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                                    errors.newPassword ? 
                                    'border-red-500 focus:ring-red-500' : 
                                    'border-gray-300 focus:ring-blue-500'
                                }`}
                                value={formData.newPassword}
                                onChange={handleChange}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                onClick={() => togglePasswordVisibility('newPassword')}
                            >
                                {showPassword.newPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            1 special character | 1 uppercase | 1 lowercase | 1 numeric
                        </p>
                        {errors.newPassword && (
                            <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                        )}
                    </div>

                    <div className="mb-6">
                        <label className="block text-[#004276] font-medium mb-2" htmlFor="confirmPassword">
                            Confirm New Password *
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword.confirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder="Confirm New Password"
                                className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 ${
                                    errors.confirmPassword ? 
                                    'border-red-500 focus:ring-red-500' : 
                                    'border-gray-300 focus:ring-blue-500'
                                }`}
                                value={formData.confirmPassword}
                                onChange={handleChange}
                            />
                            <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                                onClick={() => togglePasswordVisibility('confirmPassword')}
                            >
                                {showPassword.confirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                        )}
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={() => {
                                setFormData({
                                    currentPassword: '',
                                    newPassword: '',
                                    confirmPassword: ''
                                });
                                setErrors({
                                    currentPassword: '',
                                    newPassword: '',
                                    confirmPassword: ''
                                });
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 bg-gradient-to-r from-[#004172] to-[#3cba84] text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'
                            }`}
                        >
                            {isSubmitting ? 'Processing...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </OurLayouts>
    );
};

export default ChangePassword;